<script lang="ts" setup>
import { computed, ref } from "vue";
import {
  useRegressionRuns,
  useRegressionRun,
  useRunRegression,
  useRegressionSuites,
  useCreateRegressionSuite,
  useDeleteRegressionSuite,
  type RegressionRun,
  type RunItemStatus,
} from "@/queries/regression";
import {
  useSavedCalls,
  useCreateSavedCall,
  useUpdateSavedCall,
  useDeleteSavedCall,
  useRunSavedCall,
  type SavedCall,
  type SavedCallRunStatus,
} from "@/queries/saved-calls";
import { useAlertStore } from "@/stores/alerts-store";
import SavedCallModal, { type SaveSavedCallPayload } from "./components/SavedCallModal.vue";
import BaselineBadge from "./components/BaselineBadge.vue";
import RegressionItemValidation from "./components/RegressionItemValidation.vue";

// The Testing console: one page that both authors saved calls (create/edit/delete/run-one, baselines)
// AND runs them in batches (multi-select → run / save-as-suite, suites, results). projectId scopes
// everything to one project (per-project Testing tab); unset → org-wide across all the org's projects.
const props = defineProps<{ projectId?: string }>();
const projectIdRef = computed(() => props.projectId);

// Embedded in a project's Testing tab (projectId set) vs the org-wide top-level page (unset). The tab
// scopes to one known project, so it slims the UI: drops the redundant Project tier from the tree,
// hides suites (a cross-project, main-page concept), and hides search (a single project's call list is
// small enough to scan). Ad-hoc "Run Selected" + results still work here.
const isProjectScoped = computed(() => !!props.projectId);

const alertStore = useAlertStore();

// ── Data ───────────────────────────────────────────────────────────────────────────────────────
const { savedCalls, savedCallsLoading } = useSavedCalls(projectIdRef);
const { runs, runsLoading, refetchRuns } = useRegressionRuns(projectIdRef);
const { suites, suitesLoading } = useRegressionSuites(projectIdRef);
const { runRegressionAsync, runRegressionPending } = useRunRegression();
const { createSuiteAsync, createSuitePending } = useCreateRegressionSuite();
const { deleteSuiteAsync } = useDeleteRegressionSuite();

const { createSavedCallAsync, createSavedCallPending, createSavedCallError, createSavedCallReset } =
  useCreateSavedCall();
const { updateSavedCallAsync, updateSavedCallPending, updateSavedCallError, updateSavedCallReset } =
  useUpdateSavedCall();
const { deleteSavedCallAsync, deleteSavedCallPending } = useDeleteSavedCall();
const { runSavedCallAsync, runSavedCallPending } = useRunSavedCall();

// ── Search (filters which calls appear in the tree) ──────────────────────────────────────────────
// One search box replaces the old flat-list project/workflow dropdowns — the tree's Project→Workflow
// grouping already IS the project/workflow axis.
const searchQuery = ref("");
const filteredCalls = computed(() => {
  const q = searchQuery.value.trim().toLowerCase();
  if (!q) return savedCalls.value;
  return savedCalls.value.filter(
    (c) =>
      c.name.toLowerCase().includes(q) ||
      (c.description ?? "").toLowerCase().includes(q) ||
      (c.workflowName ?? "").toLowerCase().includes(q) ||
      JSON.stringify(c.inputs ?? {}).toLowerCase().includes(q)
  );
});

// ── Project → Workflow → Call tree ─────────────────────────────────────────────────────────────
// Each call leaf carries the full SavedCall so a row can show its last-run status + baseline and drive
// the per-row Run / Edit / Delete actions without a second lookup.
interface CallLeaf { call: SavedCall; }
interface WorkflowNode { workflowId: string; workflowName: string; calls: CallLeaf[]; }
interface ProjectNode { projectId: string; projectName: string; workflows: WorkflowNode[]; callIds: string[]; }

const tree = computed<ProjectNode[]>(() => {
  const projects = new Map<string, ProjectNode>();
  for (const c of filteredCalls.value) {
    let project = projects.get(c.projectId);
    if (!project) {
      project = { projectId: c.projectId, projectName: c.projectName ?? c.projectId, workflows: [], callIds: [] };
      projects.set(c.projectId, project);
    }
    project.callIds.push(c.id);

    let wf = project.workflows.find((w) => w.workflowId === c.workflowId);
    if (!wf) {
      wf = { workflowId: c.workflowId, workflowName: c.workflowName ?? c.workflowId, calls: [] };
      project.workflows.push(wf);
    }
    wf.calls.push({ call: c });
  }
  const sorted = [...projects.values()].sort((a, b) => a.projectName.localeCompare(b.projectName));
  for (const p of sorted) {
    p.workflows.sort((a, b) => a.workflowName.localeCompare(b.workflowName));
    for (const w of p.workflows) w.calls.sort((a, b) => a.call.name.localeCompare(b.call.name));
  }
  return sorted;
});

// Projects collapsed by default; a Set of expanded project ids (workflow tiers follow the project).
const expandedProjects = ref<Set<string>>(new Set());
function toggleProjectExpanded(projectId: string) {
  const next = new Set(expandedProjects.value);
  next.has(projectId) ? next.delete(projectId) : next.add(projectId);
  expandedProjects.value = next;
}

// ── Selection (cascading) ──────────────────────────────────────────────────────────────────────
const selectedIds = ref<Set<string>>(new Set());

// tri-state helpers: a group is checked if ALL its calls are selected, indeterminate if SOME are.
function groupState(callIds: string[]): "checked" | "indeterminate" | "unchecked" {
  if (callIds.length === 0) return "unchecked";
  const selectedCount = callIds.filter((id) => selectedIds.value.has(id)).length;
  if (selectedCount === 0) return "unchecked";
  return selectedCount === callIds.length ? "checked" : "indeterminate";
}

function setMany(callIds: string[], selected: boolean) {
  const next = new Set(selectedIds.value);
  for (const id of callIds) selected ? next.add(id) : next.delete(id);
  selectedIds.value = next;
}

function toggleOne(id: string) {
  const next = new Set(selectedIds.value);
  next.has(id) ? next.delete(id) : next.add(id);
  selectedIds.value = next;
}

function clearSelection() {
  selectedIds.value = new Set();
}

// Toggling a group selects all when not fully selected, else clears all.
function toggleProject(p: ProjectNode) {
  setMany(p.callIds, groupState(p.callIds) !== "checked");
}
function toggleWorkflow(w: WorkflowNode) {
  const ids = w.calls.map((c) => c.call.id);
  setMany(ids, groupState(ids) !== "checked");
}

// All ids across the FULL (unfiltered) list, so "select all" / suite membership isn't limited by search.
const allCallIds = computed(() => savedCalls.value.map((c) => c.id));
const allState = computed(() => groupState(allCallIds.value));
function toggleAll() {
  setMany(allCallIds.value, allState.value !== "checked");
}

const selectedCountFor = (callIds: string[]) => callIds.filter((id) => selectedIds.value.has(id)).length;

// Native checkbox `indeterminate` is a DOM-only property (not an attribute), so bind it via a directive.
const vIndeterminate = {
  mounted(el: HTMLInputElement, binding: { value: boolean }) { el.indeterminate = binding.value; },
  updated(el: HTMLInputElement, binding: { value: boolean }) { el.indeterminate = binding.value; },
};

// ── Batch run + results ──────────────────────────────────────────────────────────────────────────
// The batch expanded in the results list. When it's still running we poll it live.
const expandedRunId = ref<string | null>(null);
const { run: liveRun } = useRegressionRun(expandedRunId);

function toggleExpand(runId: string) {
  expandedRunId.value = expandedRunId.value === runId ? null : runId;
}

// The row's live data — the polled copy when expanded (so items converge in view), else the list copy.
function rowData(run: RegressionRun): RegressionRun {
  return expandedRunId.value === run.id && liveRun.value ? liveRun.value : run;
}

async function runSelected() {
  if (selectedIds.value.size === 0) return;
  try {
    const batchId = await runRegressionAsync({ savedCallIds: [...selectedIds.value] });
    expandedRunId.value = batchId; // expand + start polling the fresh batch
    await refetchRuns();
  } catch {
    alertStore.addError("Regression", "Failed to start the regression run.");
  }
}

// ── Suites ───────────────────────────────────────────────────────────────────────────────────────
const newSuiteName = ref("");

// Save the current selection as a named, reusable suite.
async function saveAsSuite() {
  const name = newSuiteName.value.trim();
  if (!name || selectedIds.value.size === 0) return;
  try {
    await createSuiteAsync({ name, savedCallIds: [...selectedIds.value] });
    newSuiteName.value = "";
    alertStore.addSuccess("Suite saved", `"${name}" saved — run it any time from the suites list.`);
  } catch {
    alertStore.addError("Regression", "Failed to save the suite.");
  }
}

// Load a suite's members into the checkbox selection (so the tree reflects it and it can be tweaked).
function loadSuite(savedCallIds: string[]) {
  selectedIds.value = new Set(savedCallIds.filter((id) => allCallIds.value.includes(id)));
}

// Run a suite directly by id (no need to load it into the selection first).
async function runSuite(suiteId: string) {
  try {
    const batchId = await runRegressionAsync({ suiteId });
    expandedRunId.value = batchId;
    await refetchRuns();
  } catch {
    alertStore.addError("Regression", "Failed to run the suite.");
  }
}

async function removeSuite(suiteId: string, name: string) {
  try {
    await deleteSuiteAsync(suiteId);
    alertStore.addSuccess("Suite deleted", `"${name}" was removed.`);
  } catch {
    alertStore.addError("Regression", "Failed to delete the suite.");
  }
}

// ── Authoring: create / edit modal ───────────────────────────────────────────────────────────────
const showModal = ref(false);
const editing = ref<SavedCall | null>(null);
const modalPending = computed(() => createSavedCallPending.value || updateSavedCallPending.value);
const modalError = computed(
  () => createSavedCallError.value?.message ?? updateSavedCallError.value?.message ?? null
);

function openCreate() {
  editing.value = null;
  createSavedCallReset();
  updateSavedCallReset();
  showModal.value = true;
}

function openEdit(call: SavedCall) {
  editing.value = call;
  createSavedCallReset();
  updateSavedCallReset();
  showModal.value = true;
}

async function onSave(payload: SaveSavedCallPayload) {
  try {
    if (editing.value) {
      const editingCall = editing.value;
      await updateSavedCallAsync({
        callId: editingCall.id,
        payload: {
          name: payload.name,
          description: payload.description,
          inputs: payload.inputs,
          projectContextId: editingCall.projectContextId ?? undefined,
        },
      });
    } else {
      await createSavedCallAsync({
        projectId: payload.projectId,
        workflowId: payload.workflowId,
        name: payload.name,
        description: payload.description,
        inputs: payload.inputs,
      });
    }
    showModal.value = false;
    editing.value = null;
  } catch {
    // Surfaced via modalError.
  }
}

// ── Per-row actions (single run / delete) ──────────────────────────────────────────────────────────
const busyCallId = ref<string | null>(null);

// The row whose ⋯ actions menu is open (Edit / Set baseline / Delete). One at a time.
const openMenuCallId = ref<string | null>(null);
function toggleMenu(id: string) {
  openMenuCallId.value = openMenuCallId.value === id ? null : id;
}
function closeMenu() {
  openMenuCallId.value = null;
}

async function runCall(call: SavedCall) {
  busyCallId.value = call.id;
  try {
    await runSavedCallAsync(call.id);
  } catch {
    // Non-fatal; last-run status won't update.
  } finally {
    busyCallId.value = null;
  }
}

async function deleteCall(call: SavedCall) {
  if (!window.confirm(`Delete saved call "${call.name}"?`)) return;
  busyCallId.value = call.id;
  try {
    await deleteSavedCallAsync(call.id);
    // Drop it from any selection so a stale id can't be run/suited.
    if (selectedIds.value.has(call.id)) toggleOne(call.id);
  } finally {
    busyCallId.value = null;
  }
}

// ── Display helpers ────────────────────────────────────────────────────────────────────────────────
function statusBadgeClass(status: SavedCallRunStatus): string {
  switch (status) {
    case "Succeeded": return "bg-success";
    case "Failed": return "bg-danger";
    case "Running": return "bg-info";
    default: return "bg-secondary";
  }
}

function itemBadgeClass(status: RunItemStatus): string {
  switch (status) {
    case "Passed": return "bg-success";
    case "Failed":
    case "Errored": return "bg-danger";
    case "Running": return "bg-info";
    default: return "bg-secondary";
  }
}

function formatDate(value: string): string {
  return new Date(value).toLocaleString();
}
</script>

<template>
  <div>
    <!-- ── SAVED CALLS ─────────────────────────────────────────────────────────────────────────
         The library. Author here; each call optionally has a baseline. Ticking a call arms the
         contextual selection bar below (its checkboxes are otherwise silent). No title above the
         section on the project tab / top-level page — their own headers already name the area. -->
    <div class="d-flex align-items-center justify-content-between gap-2 mb-2">
      <h5 class="mb-0 d-flex align-items-center gap-2">
        <i class="bi bi-telephone-outbound text-muted"></i> Saved calls
        <span v-if="allCallIds.length > 0" class="badge bg-primary rounded-pill">{{ allCallIds.length }}</span>
      </h5>
      <button class="bb-btn-primary-cta btn-sm" @click="openCreate">
        <i class="bi bi-plus me-1"></i>New<span class="d-none d-md-inline"> saved call</span>
      </button>
    </div>

    <!-- Search — hidden on the project tab, where a single project's call list is small enough to scan. -->
    <div v-if="!isProjectScoped && allCallIds.length > 0" class="mb-2">
      <input
        v-model="searchQuery"
        type="text"
        class="form-control form-control-sm"
        placeholder="Search name, description, workflow, inputs…"
      />
    </div>

    <!-- Loading / empty / the selectable Project → Workflow → Call list -->
    <div v-if="savedCallsLoading" class="text-center py-5">
      <div class="spinner-border text-secondary" role="status">
        <span class="visually-hidden">Loading saved calls…</span>
      </div>
    </div>

    <div v-else-if="allCallIds.length === 0" class="text-center py-5">
      <i class="bi bi-telephone-outbound display-1 text-muted"></i>
      <h3 class="mt-3 text-muted">No saved calls yet</h3>
      <p class="text-muted">Save a named call to one of your workflows to run it — one at a time or several together.</p>
      <button class="bb-btn-primary-cta mt-2" @click="openCreate">
        <i class="bi bi-plus-circle me-1"></i> New saved call
      </button>
    </div>

    <!-- Calls exist, but the current search matched none. -->
    <div v-else-if="tree.length === 0" class="text-center py-5 text-muted">
      <i class="bi bi-search display-4"></i>
      <p class="mt-3 mb-2">No saved calls match "{{ searchQuery.trim() }}".</p>
      <button class="btn btn-sm btn-link p-0 text-decoration-none" @click="searchQuery = ''">
        Clear search
      </button>
    </div>

    <div v-else class="list-group mb-4">
      <!-- Select all -->
      <label class="list-group-item d-flex align-items-center gap-2">
        <input
          v-indeterminate="allState === 'indeterminate'"
          class="form-check-input m-0"
          type="checkbox"
          :checked="allState === 'checked'"
          @change="toggleAll"
        />
        <span class="fw-semibold small">Select all</span>
        <span class="text-muted small ms-auto">{{ selectedIds.size }} of {{ allCallIds.length }} selected</span>
      </label>

      <template v-for="project in tree" :key="project.projectId">
        <!-- Project header — dropped on the project tab, where there's only the one known project. -->
        <div v-if="!isProjectScoped" class="list-group-item d-flex align-items-center gap-2">
          <input
            v-indeterminate="groupState(project.callIds) === 'indeterminate'"
            class="form-check-input m-0"
            type="checkbox"
            :checked="groupState(project.callIds) === 'checked'"
            @change="toggleProject(project)"
            @click.stop
          />
          <button
            type="button"
            class="btn btn-sm btn-link p-0 text-decoration-none d-flex align-items-center gap-2 flex-grow-1 text-start"
            @click="toggleProjectExpanded(project.projectId)"
          >
            <i class="bi" :class="expandedProjects.has(project.projectId) ? 'bi-chevron-down' : 'bi-chevron-right'"></i>
            <span class="fw-semibold">{{ project.projectName }}</span>
            <span class="text-muted small">
              {{ selectedCountFor(project.callIds) }}/{{ project.callIds.length }}
            </span>
          </button>
        </div>

        <!-- Workflow tiers + call leaves. Project tab: always shown (no project tier to expand). Main
             page: shown only when the project row is expanded. -->
        <template v-if="isProjectScoped || expandedProjects.has(project.projectId)">
          <template v-for="wf in project.workflows" :key="wf.workflowId">
            <label class="list-group-item d-flex align-items-center gap-2" :class="isProjectScoped ? '' : 'ps-4'">
              <input
                v-indeterminate="groupState(wf.calls.map((c) => c.call.id)) === 'indeterminate'"
                class="form-check-input m-0"
                type="checkbox"
                :checked="groupState(wf.calls.map((c) => c.call.id)) === 'checked'"
                @change="toggleWorkflow(wf)"
              />
              <i class="bi bi-diagram-3 text-muted"></i>
              <span class="small fw-medium">{{ wf.workflowName }}</span>
            </label>
            <label
              v-for="{ call } in wf.calls"
              :key="call.id"
              class="list-group-item d-flex align-items-center justify-content-between gap-2"
              :class="isProjectScoped ? 'ps-4' : 'ps-5'"
            >
              <div class="d-flex align-items-center gap-2 flex-grow-1 min-width-0">
                <input
                  class="form-check-input m-0"
                  type="checkbox"
                  :checked="selectedIds.has(call.id)"
                  @change="toggleOne(call.id)"
                />
                <span class="text-truncate">{{ call.name }}</span>
                <span class="badge flex-shrink-0" :class="statusBadgeClass(call.lastRunStatus)">
                  {{ call.lastRunStatus === "NeverRun" ? "Never" : call.lastRunStatus }}
                </span>
                <BaselineBadge :has-baseline="call.hasBaseline" :count-policy="call.baselineCountPolicy" />
              </div>
              <!-- Per-row actions. Inline Run keeps the author-iterate loop one click; the rest
                   collapse behind ⋯ so the row reads as information, not a form.
                   @click.stop.prevent so acting on a row never toggles its checkbox. -->
              <div class="d-flex align-items-center gap-1 flex-shrink-0 position-relative">
                <button
                  class="bb-btn-secondary-cta btn-sm"
                  title="Run this call now"
                  :disabled="runSavedCallPending && busyCallId === call.id"
                  @click.stop.prevent="runCall(call)"
                >
                  <span
                    v-if="runSavedCallPending && busyCallId === call.id"
                    class="spinner-border spinner-border-sm"
                    role="status"
                  ></span>
                  <template v-else><i class="bi bi-play"></i> Run</template>
                </button>
                <button
                  class="bb-btn-secondary-cta btn-sm"
                  title="More actions"
                  @click.stop.prevent="toggleMenu(call.id)"
                >
                  <i class="bi bi-three-dots"></i>
                </button>
                <div
                  v-if="openMenuCallId === call.id"
                  class="dropdown-menu show shadow-sm"
                  style="position: absolute; right: 0; top: 100%; z-index: 10"
                >
                  <button class="dropdown-item" @click.stop.prevent="closeMenu(); openEdit(call)">
                    <i class="bi bi-pencil me-2"></i>Edit
                  </button>
                  <button class="dropdown-item" @click.stop.prevent="closeMenu(); openEdit(call)">
                    <i class="bi bi-clipboard-check me-2"></i>{{ call.hasBaseline ? "Manage baseline" : "Set baseline" }}
                  </button>
                  <div class="dropdown-divider"></div>
                  <button
                    class="dropdown-item text-danger"
                    :disabled="deleteSavedCallPending && busyCallId === call.id"
                    @click.stop.prevent="closeMenu(); deleteCall(call)"
                  >
                    <i class="bi bi-trash me-2"></i>Delete
                  </button>
                </div>
              </div>
            </label>
          </template>
        </template>
      </template>
    </div>

    <!-- ── Contextual selection bar ──────────────────────────────────────────────────────────────
         Appears only once ≥1 call is ticked, giving the checkboxes an obvious verb: run them, or
         (main page) name them as a reusable suite. Absorbs the old top-level "Run Selected" button
         and the floating suite-name strip. -->
    <Transition name="bb-selbar">
      <div
        v-if="selectedIds.size > 0"
        class="d-flex flex-wrap align-items-center gap-2 mb-4 p-2 rounded border bg-body-secondary"
      >
        <span class="fw-semibold small ms-1">{{ selectedIds.size }} selected</span>
        <button
          class="bb-btn-primary-cta btn-sm"
          :disabled="runRegressionPending"
          @click="runSelected"
        >
          <span v-if="runRegressionPending" class="spinner-border spinner-border-sm me-1" role="status"></span>
          <i v-else class="bi bi-play-circle me-1"></i>Run {{ selectedIds.size }}
        </button>

        <!-- Save-as-suite: a cross-project, main-page concept — hidden on the per-project tab. -->
        <template v-if="!isProjectScoped">
          <div class="vr mx-1"></div>
          <input
            v-model="newSuiteName"
            type="text"
            class="form-control form-control-sm"
            style="max-width: 16rem"
            placeholder="Name this selection…"
            @keyup.enter="saveAsSuite"
          />
          <button
            class="bb-btn-secondary-cta btn-sm"
            :disabled="createSuitePending || !newSuiteName.trim()"
            title="Save the selected calls as a reusable named suite"
            @click="saveAsSuite"
          >
            <span v-if="createSuitePending" class="spinner-border spinner-border-sm me-1" role="status"></span>
            <i v-else class="bi bi-bookmark-plus me-1"></i>Save as suite
          </button>
        </template>

        <button class="bb-btn-cancel btn-sm ms-auto" @click="clearSelection">Clear</button>
      </div>
    </Transition>

    <!-- ── SUITES ────────────────────────────────────────────────────────────────────────────────
         Named selections you can re-run any time. Cross-project, main-page only — hidden on the
         per-project tab, where ad-hoc "Run" covers the need. -->
    <template v-if="!isProjectScoped && allCallIds.length > 0">
      <h5 class="mb-1 mt-4 d-flex align-items-center gap-2">
        <i class="bi bi-collection text-muted"></i> Suites
        <span v-if="suites.length > 0" class="badge bg-primary rounded-pill">{{ suites.length }}</span>
      </h5>
      <p class="text-muted small mb-2">Named selections you can re-run any time.</p>

      <div v-if="suites.length > 0" class="list-group mb-4">
        <div
          v-for="suite in suites"
          :key="suite.id"
          class="list-group-item d-flex align-items-center justify-content-between gap-2 py-2"
        >
          <div class="d-flex align-items-center gap-2 min-width-0">
            <i class="bi bi-collection text-muted"></i>
            <span class="fw-medium text-truncate">{{ suite.name }}</span>
            <span class="text-muted small flex-shrink-0">
              {{ suite.savedCallIds.length }} call{{ suite.savedCallIds.length === 1 ? "" : "s" }}
            </span>
          </div>
          <div class="d-flex align-items-center gap-2 flex-shrink-0">
            <button
              class="bb-btn-secondary-cta btn-sm"
              title="Load this suite's calls into the selection"
              @click="loadSuite(suite.savedCallIds)"
            >
              <i class="bi bi-check2-square"></i> Load
            </button>
            <button
              class="bb-btn-primary-cta btn-sm"
              :disabled="runRegressionPending"
              @click="runSuite(suite.id)"
            >
              <i class="bi bi-play"></i> Run
            </button>
            <button
              class="bb-btn-danger btn-sm"
              title="Delete this suite"
              @click="removeSuite(suite.id, suite.name)"
            >
              <i class="bi bi-trash"></i> Delete
            </button>
          </div>
        </div>
      </div>
      <p v-else-if="!suitesLoading" class="text-muted small mb-4">
        No suites yet — tick calls above and save them as a named suite to re-run later.
      </p>
    </template>

    <!-- ── RUNS ──────────────────────────────────────────────────────────────────────────────────
         History. One run may have a single item (what used to be an "ad-hoc" run) or many. -->
    <h5 class="mb-1 mt-4 d-flex align-items-center gap-2">
      <i class="bi bi-clock-history text-muted"></i> Runs
    </h5>
    <p class="text-muted small mb-2">Every run — one call or many — with each result checked against its baseline.</p>

    <div v-if="runsLoading" class="text-center py-5">
      <div class="spinner-border text-secondary" role="status">
        <span class="visually-hidden">Loading runs…</span>
      </div>
    </div>

    <div v-else-if="runs.length === 0" class="text-center py-5 text-muted">
      <i class="bi bi-clipboard-data display-1"></i>
      <h3 class="mt-3">No runs yet</h3>
      <p class="mb-0">Run a call — one or several — to see pass/fail outcomes here.</p>
    </div>

    <div v-else class="list-group">
      <div v-for="run in runs" :key="run.id" class="list-group-item">
        <div
          class="d-flex justify-content-between align-items-center cursor-pointer"
          @click="toggleExpand(run.id)"
        >
          <div class="d-flex align-items-center gap-3">
            <i class="bi" :class="expandedRunId === run.id ? 'bi-chevron-down' : 'bi-chevron-right'"></i>
            <div>
              <div class="fw-medium">{{ formatDate(run.startedAtUtc) }}</div>
              <div class="small text-muted">
                {{ rowData(run).items.length }} call{{ rowData(run).items.length === 1 ? "" : "s" }}
              </div>
            </div>
          </div>
          <div class="d-flex align-items-center gap-2">
            <span
              class="badge"
              :class="rowData(run).status === 'Completed' ? 'bg-secondary' : 'bg-info'"
            >{{ rowData(run).status }}</span>
            <span class="badge bg-success">{{ rowData(run).passedCount }} passed</span>
            <span v-if="rowData(run).failedCount > 0" class="badge bg-danger">
              {{ rowData(run).failedCount }} failed
            </span>
            <span v-if="rowData(run).pendingCount > 0" class="badge bg-info">
              {{ rowData(run).pendingCount }} running
            </span>
          </div>
        </div>

        <!-- Expanded per-item detail -->
        <div v-if="expandedRunId === run.id" class="mt-3 pt-3 border-top">
          <div v-for="item in rowData(run).items" :key="item.workflowRunId" class="py-1">
            <div class="d-flex justify-content-between align-items-center">
              <div>
                <span class="fw-medium">{{ item.savedCallName }}</span>
                <span v-if="item.message" class="text-muted small ms-2">{{ item.message }}</span>
              </div>
              <span class="badge" :class="itemBadgeClass(item.status)">{{ item.status }}</span>
            </div>
            <RegressionItemValidation
              :saved-call-id="item.savedCallId"
              :workflow-run-id="item.workflowRunId"
              :validation-result-id="item.validationResultId"
            />
          </div>
        </div>
      </div>
    </div>

    <SavedCallModal
      v-model:show="showModal"
      :editing="editing"
      :pending="modalPending"
      :error-message="modalError"
      :project-id="projectId"
      :existing-calls="savedCalls"
      @save="onSave"
    />
  </div>
</template>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}
.min-width-0 {
  min-width: 0;
}
.bb-selbar-enter-active,
.bb-selbar-leave-active {
  transition: opacity 0.15s ease;
}
.bb-selbar-enter-from,
.bb-selbar-leave-to {
  opacity: 0;
}
</style>
